import type { FragmentType } from '__generated__';
import { getFragmentData } from '__generated__';
import { AUTH_CHALLENGE_FIELDS } from 'graphql/auth/fragments/AuthChallenge.gql';
import { useCallback, useMemo, useState } from 'react';

import Form from 'components/Form/Form';
import { FormFieldInterface } from 'components/Form/Form.types';
import { useAuthContext } from 'context/auth.context';
import useAuthChallengeMutation from 'hooks/auth/mutations/useAuthChallengeMutation';
import validator from 'validator';

interface LoginChallengeProps {
  tenantId: number;
  email: string;
  challenge: FragmentType<typeof AUTH_CHALLENGE_FIELDS>;
  onError?: (error: string) => void;
  onNewChallenge: (newChallenge: FragmentType<typeof AUTH_CHALLENGE_FIELDS>) => void;
  onComplete?: () => void;
}

type Props = LoginChallengeProps;

const LoginChallenge = ({
  tenantId,
  email,
  challenge: challengeRef,
  onError,
  onNewChallenge,
  onComplete,
}: Props) => {
  const challenge = getFragmentData(AUTH_CHALLENGE_FIELDS, challengeRef);
  const { login } = useAuthContext();
  const [authChallenge] = useAuthChallengeMutation();
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  const fields: FormFieldInterface[] = useMemo(
    () => [
      {
        label: 'New Password',
        onChange: (value) => setNewPassword(value),
        type: 'password',
        value: newPassword,
        containedLabel: true,
      },
      {
        label: 'Confirm New Password',
        onChange: (value) => setConfirmPassword(value),
        type: 'password',
        value: confirmPassword,
        containedLabel: true,
      },
    ],
    [confirmPassword, newPassword]
  );

  const handleSubmit = useCallback(async () => {
    if (onError) {
      onError('');
    }
    const isStrongPassword = validator.isStrongPassword(newPassword, {
      minLength: 8,
      minLowercase: 1,
      minUppercase: 1,
      minNumbers: 1,
      minSymbols: 1,
    });

    if (!isStrongPassword) {
      if (onError) {
        onError(
          'Password must be at least 8 characters with at least 1 uppercase, 1 number, and 1 symbol'
        );
      }
      return false;
    }

    if (newPassword !== confirmPassword) {
      if (onError) {
        onError('Passwords must match');
      }
      return false;
    }

    const result = await authChallenge({
      variables: {
        input: {
          tenantId,
          userEmail: email,
          newPassword,
          session: challenge.session,
        },
      },
    }).catch((error: Error) => {
      onError?.(error.message);
      return undefined;
    });
    if (!result) return;

    if (result.data?.authChallenge) {
      const response = result.data.authChallenge;
      if (response.success) {
        if (response.data) {
          login(response.data);
          if (onComplete) {
            onComplete();
          }
        } else if (response.challenge) {
          onNewChallenge(response.challenge);
        }
      } else {
        if (onError) {
          onError(response.error || '');
        }
      }
    } else {
      if (onError) {
        onError('An unexpected error occurred');
      }
    }
  }, [
    authChallenge,
    challenge.session,
    confirmPassword,
    email,
    login,
    newPassword,
    onError,
    onNewChallenge,
    tenantId,
  ]);

  return (
    <Form
      fields={fields}
      buttonText='Save & Login'
      onSubmit={handleSubmit}
      submitButtonProps={{
        fullWidth: true,
        color: 'primary',
        variant: 'contained',
      }}
      sx={{ p: 0 }}
    />
  );
};

export default LoginChallenge;
